import { ChevronLeft } from "lucide-react";
import React from "react";
import { useImmer } from "use-immer";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useCurrentPriceVersion } from "@src/api/intelligems/get-live-prices";
import { useAdjustBilling } from "@src/api/stripe/adjust-billing";
import { FormCheckboxes } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/FormCheckboxes";
import { CancelItems } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/Forms/CancelForm/cancel-items";
import { CloseIcon } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/Forms/CloseIcon";
import { getPauseCancelReasons } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/Forms/utils";
import { ContentProps } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/types";

export const CancelForm = ({
  handleClose,
  stripeSubscriptionLinkId,
}: ContentProps) => {
  const [items, setItems] = useImmer(CancelItems);

  const adjustBilling = useAdjustBilling();
  const version = useCurrentPriceVersion();

  const onItemClick = (value: string, checked: boolean) => {
    setItems((draft) => {
      const filteredItems = structuredClone(CancelItems).filter(
        (item) => item.value === value
      );
      for (const item of filteredItems) {
        item.checked = true;
      }
      draft = filteredItems;
      return draft;
    });
  };

  const onSubItemClick = (itemValue: string, subItemValue: string) => {
    setItems((draft) => {
      const itemIndex = draft.findIndex((item) => item.value === itemValue);
      if (itemIndex > -1) {
        const subItemIndex = draft[itemIndex].items.findIndex(
          (subItem) => subItem.value === subItemValue
        );

        for (const [i, subItem] of draft[itemIndex].items.entries()) {
          subItem.checked = i === subItemIndex;
        }
      }

      return draft;
    });
  };

  const onBackClick = () => {
    setItems(structuredClone(CancelItems));
  };

  const handleCancelClick = () => {
    adjustBilling.mutate(
      {
        pathParams: undefined,
        queryParams: undefined,
        body: {
          action: "cancel",
          stripeSubscriptionLinkId,
          reasons: getPauseCancelReasons(items),
        },
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };
  return (
    <>
      <DialogTitle sx={{ padding: "0 0 20px 0" }}>
        <IgTypography variant={"h2"}>Sorry to See You Go!</IgTypography>
        <CloseIcon handleClose={handleClose} />
      </DialogTitle>
      <DialogContent sx={{ padding: "0 0 16px 0", minWidth: "500px" }}>
        <Grid container item direction={"column"}>
          <Grid item sx={{ marginTop: "2px", marginBottom: "10px" }}>
            <IgTypography>
              What's your reason for cancelling your plan?
            </IgTypography>
          </Grid>
          <FormCheckboxes
            items={items}
            disabled={adjustBilling.isPending}
            onItemClick={(value, checked) => onItemClick(value, checked)}
            onSubItemClick={(itemValue, subItemValue) =>
              onSubItemClick(itemValue, subItemValue)
            }
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent={"space-between"} alignItems={"center"}>
          <Grid item>
            {items.some((i) => i.checked) ? (
              <IgButton
                loading={adjustBilling.isPending}
                startIcon={<ChevronLeft size={18} />}
                onClick={() => onBackClick()}
              >
                Back
              </IgButton>
            ) : null}
          </Grid>
          <Grid item>
            <IgButton
              variant={"contained"}
              onClick={() => handleCancelClick()}
              loading={adjustBilling.isPending}
            >
              Confirm Plan Cancellation
            </IgButton>
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};
